import { getAccessToken } from './authStorage';
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    // Set when connect() opens a socket and cleared once that socket
    // either reaches OPEN (isConnected = true takes over) or fails. The
    // three layout components (LayoutUnified, ControlCenterLayout,
    // OperatorLayout) each call connect() from a useEffect on mount, and
    // their `if (!websocketService.isConnected)` guards all see false at
    // login time — so without this flag three separate WebSocket sockets
    // get opened simultaneously. Issue #732.
    this.isConnecting = false;
    this.listeners = {
      statusChange: [],
      observatoryUpdate: [],
      observationUpdate: [],
      notification: [],
      telemetryUpdate: [],
      guiderUpdate: [],
      astronomicalUpdate: [],
      // Add snake_case versions for compatibility
      observation_created: [],
      observation_updated: [],
      observation_assigned: [],
      observation_started: [],
      observation_completed: [],
      observation_progress: [],
      observation_deleted: [],
      observation_failed: [],
      observation_aborted: [],
      observation_cancelled: [],
      observation_expired: [],
      observation_moved: [],
      file_updated: [],
      log_upload_complete: [],
      observation_log_entry: [],
      project_history_entry: [],
    };
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
  }

  connect() {
    // Use native WebSocket to match server implementation

    // Idempotency: if a socket is already attached (open or being opened)
    // we don't want to open a second one. The `isConnected` field flips
    // to true only at onopen; without `isConnecting` covering the
    // pending-open window, two near-simultaneous connect() calls from
    // sibling components race and each spawn their own WebSocket.
    if (this.isConnected || this.isConnecting) {
      return;
    }
    this.isConnecting = true;

    // Determine WebSocket URL based on current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    // Only include port if explicitly specified (not default 80/443)
    const port = window.location.port;
    const portSuffix = port ? `:${port}` : '';

    // Use the web-specific WebSocket path
    const wsUrl = `${protocol}//${host}${portSuffix}/ws/web`;

    console.log('[WebSocket] Attempting to connect to:', wsUrl);

    try {
      this.socket = new WebSocket(wsUrl);
      console.log('[WebSocket] WebSocket object created');

      this.socket.onopen = () => {
        console.log('[WebSocket] Connection opened successfully');
        this.isConnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.emit('statusChange', 'connected');

        // Send authentication with JWT token
        const token = getAccessToken();
        if (token) {
          console.log('[WebSocket] Sending authentication');
          this.sendMessage({
            type: 'authenticate',
            token: token,
            client_type: 'web',
            client_id: 'admin-gui',
            subscriptions: [
              'observatory_status',
              'observation_updates',
              'session_changes',
              'system_alerts'
            ]
          });
        } else {
          console.warn('[WebSocket] No auth token found in localStorage');
        }
      };

      this.socket.onclose = (event) => {
        this.isConnected = false;
        this.isConnecting = false;
        this.socket = null;
        this.emit('statusChange', 'disconnected');

        // Attempt reconnection
        this.attemptReconnect();
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnected = false;
        this.isConnecting = false;
        this.emit('statusChange', 'error');
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Route messages based on type
          switch (message.type || message.message_type) {
            case 'observatory_status':
              this.emit('observatoryUpdate', {
                type: 'status_change',
                observatory: message.observatory,
                status: message.status,
                timestamp: message.timestamp,
              });
              break;
              
            case 'observatory_status_update':
              this.emit('observatoryUpdate', {
                type: 'status_update',
                observatory_id: message.observatory_id,
                observatory_name: message.observatory_name,
                status: message.status,
                plugin_connected: message.plugin_connected,
                timestamp: message.timestamp,
              });
              break;
              
            case 'observatory_connected':
              this.emit('observatoryUpdate', {
                type: 'connected',
                observatory: message.observatory,
                session: message.session,
                timestamp: message.timestamp,
              });
              
              this.emit('notification', {
                type: 'info',
                message: `Observatory "${message.observatory?.name || 'Unknown'}" connected`,
                timestamp: message.timestamp,
              });
              break;
              
            case 'observatory_disconnected':
              this.emit('observatoryUpdate', {
                type: 'disconnected',
                observatory: message.observatory,
                reason: message.reason,
                session_summary: message.session_summary,
                timestamp: message.timestamp,
              });

              this.emit('notification', {
                type: 'warning',
                message: `Observatory "${message.observatory?.name || 'Unknown'}" disconnected (${message.reason})`,
                timestamp: message.timestamp,
              });
              break;

            case 'observatory_registered':
              this.emit('observatoryUpdate', {
                type: 'registered',
                observatory_id: message.observatory_id,
                observatory_name: message.observatory_name,
                timestamp: message.timestamp,
              });

              this.emit('notification', {
                type: 'info',
                message: `New observatory "${message.observatory_name || 'Unknown'}" registered`,
                timestamp: message.timestamp,
              });
              break;

            case 'observatory_deleted':
              // Dedicated event name so AuthContext / OperatorLayout can
              // react (refresh creds, drop the view off the deleted
              // observatory). Without this case the message hit the
              // default branch and listeners registered via
              // `.on('observatory_deleted', ...)` never fired.
              this.emit('observatory_deleted', {
                observatory_id: message.observatory_id,
                observatory_name: message.observatory_name,
                removed: message.removed,
                timestamp: message.timestamp,
              });
              this.emit('notification', {
                type: 'warning',
                message: `Observatory "${message.observatory_name || 'Unknown'}" was ${message.removed ? 'removed' : 'deregistered'}`,
                timestamp: message.timestamp,
              });
              break;
              
            case 'observation_created':
              this.emit('observation_created', {
                type: 'created',
                observation_id: message.observation_id,
                project_id: message.project_id,
                target_name: message.target_name,
                status: message.status,
                priority: message.priority,
                assigned_to: message.assigned_to,
                timestamp: message.timestamp,
              });
              break;

            case 'observation_started':
              // Emit with the exact event name expected by observation pages
              this.emit('observation_started', {
                type: 'started',
                observation_id: message.observation_id,
                observation_name: message.observation_name,
                observatory_id: message.observatory_id,
                status: message.status,
                started_at: message.timestamp,
                timestamp: message.timestamp,
              });
              // Also emit the legacy event name
              this.emit('observationUpdate', {
                type: 'started',
                observation_id: message.observation_id,
                observatory_id: message.observatory_id,
                status: message.status,
                timestamp: message.timestamp,
              });
              break;
              
            case 'observation_completed':
              // Emit with the exact event name expected by ObservationList.js
              this.emit('observation_completed', {
                type: 'completed',
                observation_id: message.observation_id,
                observation_name: message.observation_name,
                observatory_id: message.observatory_id,
                status: message.status,
                timestamp: message.timestamp,
              });
              // Also emit the legacy event name
              this.emit('observationUpdate', {
                type: 'completed',
                observation_id: message.observation_id,
                observatory_id: message.observatory_id,
                status: message.status,
                timestamp: message.timestamp,
              });

              this.emit('notification', {
                type: 'success',
                message: `Observation completed`,
                timestamp: message.timestamp,
              });
              break;

            case 'observation_deleted':
              this.emit('observation_deleted', {
                type: 'deleted',
                observation_id: message.observation_id,
                project_id: message.project_id,
                timestamp: message.timestamp,
              });
              break;

            case 'observation_status_changed':
              this.emit('observationUpdate', {
                type: 'status_changed',
                observation_id: message.observation_id,
                observatory_id: message.observatory_id,
                status: message.status,
                timestamp: message.timestamp,
              });
              break;
              
            case 'observation_aborted':
              this.emit('observation_aborted', {
                type: 'aborted',
                observation_id: message.observation_id,
                observation_name: message.observation_name,
                observatory_id: message.observatory_id,
                reason: message.reason,
                timestamp: message.timestamp,
              });
              break;

            case 'observation_failed':
              this.emit('observation_failed', {
                type: 'failed',
                observation_id: message.observation_id,
                observation_name: message.observation_name,
                observatory_id: message.observatory_id,
                reason: message.reason,
                timestamp: message.timestamp,
              });
              break;

            case 'observation_cancelled':
              this.emit('observation_cancelled', {
                type: 'cancelled',
                observation_id: message.observation_id,
                observation_name: message.observation_name,
                observatory_id: message.observatory_id,
                reason: message.reason,
                timestamp: message.timestamp,
              });
              break;
              
            case 'observation_assigned':
              console.log('[DEBUG] WebSocket received observation_assigned:', JSON.stringify(message));
              // Emit with the exact event name expected by ObservationList.js
              this.emit('observation_assigned', {
                type: 'assigned',
                observation_id: message.observation_id,
                observation_name: message.observation_name,
                observatory_id: message.observatory_id,
                status: 'assigned',
                timestamp: message.timestamp,
              });
              // Also emit the legacy event name
              this.emit('observationUpdate', {
                type: 'assigned',
                observation_id: message.observation_id,
                observatory_id: message.observatory_id,
                status: 'assigned',
                timestamp: message.timestamp,
              });
              break;
              
            case 'observation_updated':
              // Emit the event with the same name that ObservationList.js expects
              // Pass through ALL fields from the server message
              this.emit('observation_updated', {
                ...message,
                type: 'updated'
              });
              // Also emit the legacy event name for backward compatibility
              this.emit('observationUpdate', {
                ...message,
                type: 'updated'
              });
              break;

            case 'observation_moved':
              // Project reassignment — list views regroup, detail views refetch.
              this.emit('observation_moved', { ...message });
              // Surface as a generic update too so listeners that already
              // refetch on observationUpdate don't need a separate listener.
              this.emit('observationUpdate', { ...message, type: 'moved' });
              break;

            case 'file_updated':
              // Handle file status updates (e.g., plate solve complete, preview ready)
              console.log('[WebSocket] Received file_updated:', message);
              this.emit('file_updated', {
                file_id: message.file_id,
                plate_solve_status: message.plate_solve_status,
                plate_solve_result: message.plate_solve_result,
                quality_metrics: message.quality_metrics,
                quality_analysis_status: message.quality_analysis_status,
                preview_url: message.preview_url,
                processing_status: message.processing_status,
                // #1206 — carry the failure reason so a live failure updates the
                // chip without waiting for a refetch.
                processing_error: message.processing_error,
                processing_error_class: message.processing_error_class,
                timestamp: message.timestamp
              });
              break;

            case 'pipeline:job:completed':
              // Pipeline processing finished - treat as observation update so detail page reloads
              console.log('[WebSocket] Received pipeline:job:completed:', message);
              if (message.data?.observationId) {
                this.emit('observationUpdate', {
                  observation_id: message.data.observationId,
                  type: 'updated',
                  pipeline_completed: true,
                });
              }
              break;

            case 'log_upload_complete':
              // Handle log upload/analysis completion
              console.log('[WebSocket] Received log_upload_complete:', message);
              this.emit('log_upload_complete', {
                log_entry_id: message.log_entry_id,
                observatory_id: message.observatory_id,
                observatory_name: message.observatory_name,
                error_count: message.error_count,
                warning_count: message.warning_count,
                critical_count: message.critical_count,
                total_count: message.total_count,
                timestamp: message.timestamp
              });
              break;

            case 'observation_log_entry':
              this.emit('observation_log_entry', {
                observation_id: message.observation_id,
                observation_name: message.observation_name,
                observatory_id: message.observatory_id,
                entry: message.entry,
              });
              break;

            case 'project_history_entry':
              // Project audit-trail event (issue #883) — the Project History
              // view subscribes and prepends matching rows live.
              this.emit('project_history_entry', {
                project_id: message.project_id,
                event: message.event,
              });
              break;

            case 'organization_history_entry':
              // Organization audit-trail event — the Organization History
              // view subscribes and prepends matching rows live.
              this.emit('organization_history_entry', {
                organization_id: message.organization_id,
                event: message.event,
              });
              break;

            case 'observation_progress':
              // Handle real-time progress updates during observation execution
              this.emit('observation_progress', {
                type: 'progress_update',
                observation_id: message.observation_id,
                observatory_id: message.observatory_id,
                frames_captured: message.frames_captured,
                frames_expected: message.frames_expected,
                progress_percent: message.progress_percent,
                status: message.status,
                timestamp: message.timestamp,
              });
              // Also emit legacy event name
              this.emit('observationUpdate', {
                type: 'progress_update',
                observation_id: message.observation_id,
                observatory_id: message.observatory_id,
                frames_captured: message.frames_captured,
                frames_expected: message.frames_expected,
                progress_percent: message.progress_percent,
                status: message.status,
                timestamp: message.timestamp,
              });
              console.log(`Real-time progress: ${message.frames_captured}/${message.frames_expected} (${message.progress_percent}%)`);
              break;
              
            case 'system_alert':
              this.emit('notification', {
                type: message.severity || 'warning',
                message: message.message,
                details: message.details,
                timestamp: message.timestamp,
              });
              break;
              
            case 'session_update':
              this.emit('observatoryUpdate', {
                type: 'session_update',
                observatory: message.observatory,
                session: message.session,
                timestamp: message.timestamp,
              });
              break;
              
            case 'telemetry_update':
              console.debug('[WebSocket] Received telemetry_update for observatory:', message.observatory_id);
              this.emit('telemetryUpdate', {
                observatory_id: message.observatory_id,
                observatory_name: message.observatory_name,
                equipment_status: message.data?.equipment_status,
                system_status: message.data?.system_status,
                timestamp: message.timestamp,
              });
              break;

            case 'guider_update':
              this.emit('guiderUpdate', {
                observatory_id: message.observatory_id,
                guider: message.guider,
                timestamp: message.timestamp,
              });
              break;

            case 'astronomical_update':
              this.emit('astronomicalUpdate', {
                observatory_id: message.observatory_id,
                observatory_name: message.observatory_name,
                astronomical_data: message.astronomical_data,
                timestamp: message.timestamp,
              });
              break;

            // Admin-allocated reservations (blocked time). The switch above
            // would otherwise drop these silently — leaving subscribers in
            // TimelineView / ScheduleAnalyticsPane / the reservations admin
            // page never notified, so the schedule didn't refresh after a
            // cancel until a manual reload.
            // An interactive (real-time) session started, changed or ended on an observatory.
            // Only the id is broadcast; useRealtimeSession refetches the details.
            case 'rt_session_changed':
              this.emit('rt_session_changed', { observatory_id: message.observatory_id });
              break;

            case 'reservation_created':
            case 'reservation_updated':
            case 'reservation_cancelled':
              this.emit(message.type, {
                type: message.type,
                reservation_id: message.reservation_id,
                observatory_id: message.observatory_id,
                cancellation_reason: message.cancellation_reason,
                effective_end_time: message.effective_end_time,
                timestamp: message.timestamp,
              });
              break;
              
            case 'auth_required':
              // Issue #732: the server sends `auth_required` the moment a
              // socket connects, but we already send `authenticate`
              // unconditionally from socket.onopen below. Responding here
              // would put a second authenticate message on the wire racing
              // the first — which is exactly what produced the doubled
              // `authenticated as ...` log lines. The eager send in
              // onopen is the single source of truth; the server keeps
              // auth_required as a hint, we don't have to act on it.
              console.debug('WebSocket auth_required noted; eager onopen already sent authenticate');
              break;
              
            case 'authenticated':
              // Authentication successful
              console.log('WebSocket authenticated successfully');
              this.emit('statusChange', 'authenticated');
              break;

            case 'user_created':
            case 'user_updated':
            case 'user_deleted':
              // Admin-side user lifecycle. Fan out as a window message
              // so UsersPane / PeopleCard / UsersFullView refresh in
              // place without waiting for their next poll.
              this.emit('userChanged', { type: message.type, user_id: message.user_id });
              try { window.postMessage({ type: 'userChanged', user_id: message.user_id }, '*'); } catch (_) { /* */ }
              break;

            case 'system_motd_created':
            case 'observatory_motd_created': {
              // Server emits these when an admin creates a system /
              // organization / project / observatory MOTD. (The first
              // four all flow through POST /v1/system/motd; the server
              // attaches target_scope so we can label the live event
              // correctly.) SystemMotdDialog listens and re-fetches
              // /v1/user/motd/pending so the acknowledge dialog appears
              // without forcing the user to log out and back in.
              const scope = message.type === 'observatory_motd_created'
                ? 'observatory'
                : (message.target_scope || 'system');
              this.emit('motdReceived', {
                scope,
                observatory_id: message.observatory_id,
                target: message.target,
                motd: message.motd,
              });
              // Re-broadcast as a window message so non-WS-aware
              // components (AnnouncementsCard, AnnouncementsPane,
              // PeopleCard) can refresh their counts/tables in place.
              try { window.postMessage({ type: 'motdChanged' }, '*'); } catch (_) { /* */ }
              break;
            }

            default:
              console.log('Unhandled WebSocket message type:', message.type || message.message_type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.isConnecting = false;
      this.emit('statusChange', 'error');
      this.attemptReconnect();
    }
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.socket) {
      console.log('Disconnecting WebSocket');
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.emit('statusChange', 'disconnected');
    }
  }

  attemptReconnect() {
    if (this.reconnectTimeout) {
      return; // Already scheduled
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.emit('notification', {
        type: 'error',
        message: 'Lost connection to server. Please refresh the page.',
        timestamp: new Date().toISOString(),
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (!this.isConnected) {
        this.connect();
      }
    }, delay);
  }

  // Send message through WebSocket
  sendMessage(message) {
    console.log('sendMessage called with:', message);
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('Sending message via WebSocket');
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('Cannot send message - WebSocket not connected. State:', this.socket?.readyState);
    }
  }

  // Event listener management
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  // Convenience methods
  onStatusChange(callback) {
    this.on('statusChange', callback);
  }

  onObservatoryUpdate(callback) {
    this.on('observatoryUpdate', callback);
  }

  // Counterpart to onObservatoryUpdate. Components that subscribe on mount
  // MUST call this on unmount — the listener array lives on this singleton
  // for the whole session, so a missing off() means every remount stacks
  // another handler and each incoming message is processed N times.
  offObservatoryUpdate(callback) {
    this.off('observatoryUpdate', callback);
  }

  onObservationUpdate(callback) {
    this.on('observationUpdate', callback);
  }

  offObservationUpdate(callback) {
    this.off('observationUpdate', callback);
  }

  onNotification(callback) {
    this.on('notification', callback);
  }

  // Send admin commands
  sendCommand(command, data) {
    this.sendMessage({
      message_type: 'admin_command',
      command,
      data,
      timestamp: new Date().toISOString(),
    });
  }

  // Request real-time data
  requestObservatoryStatus(observatoryId) {
    this.sendCommand('get_observatory_status', { observatoryId });
  }

  requestSystemStatus() {
    this.sendCommand('get_system_status', {});
  }

  // Telemetry update methods
  onTelemetryUpdate(callback) {
    this.on('telemetryUpdate', callback);
  }

  offTelemetryUpdate(callback) {
    this.off('telemetryUpdate', callback);
  }

  // Guider update methods (issue #673: dedicated channel to keep telemetry_update
  // contract intact — guider arrives independently and consumers subscribe here)
  onGuiderUpdate(callback) {
    this.on('guiderUpdate', callback);
  }

  offGuiderUpdate(callback) {
    this.off('guiderUpdate', callback);
  }

  // Astronomical update methods
  onAstronomicalUpdate(callback) {
    this.on('astronomicalUpdate', callback);
  }

  offAstronomicalUpdate(callback) {
    this.off('astronomicalUpdate', callback);
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();